import React, { useCallback, useState } from "react";
import { formaters } from "vincent-utils";
import {
  highlightedRdvAtom,
  useSelectedPatientIds,
} from "../../main/state/globalState";
import { type Dated, Timed, Transforms } from "cadendar-shared";
import styles from "./RdvCase.module.css";
import classNames from "classnames";
import RdvCaseIcons from "./RdvCaseIcons.tsx";
import { useDroppable } from "@dnd-kit/core";
import { useAtomValue, useSetAtom } from "jotai";

const getDiffInHours = (a: Date, b: Date) => {
  return (a.getTime() - b.getTime()) / 1000 / 3600;
};

const rdvColors = {
  1: "#71FF71",
  2: "yellow",
  5: "skyblue",
  10: "mediumseagreen",
  11: "red",
  12: "red",
  13: "#B870DB",
  14: "pink",
  15: "#5c5c5c",
  18: "#B870DB",
  21: "red",
  22: "red",
  25: "#d0cbff",
  26: "#71FF71",
  28: "#2d187a",
} as { [type: string]: string };

const fontColor = {
  15: "white",
  28: "white",
  // 29: "white",
} as { [type: number]: string };

const getRdvColor = (
  rdv:
    | { tag: "closed" | "empty" | "closedByHoraire" | "fake" }
    | { tag: "rdv"; type: number },
) => {
  if (rdv.tag === "closed") {
    return "rgb(92,92,92)";
  }
  if (rdv.tag === "empty") {
    return "#f1f1d9";
  }
  if (rdv.tag === "closedByHoraire") {
    //return "#5c5c5c";
    return "rgb(192,192,192)";
  }
  if (rdv.tag === "rdv") {
    const color = rdvColors[rdv.type];
    return color || "var(--color-beige)";
  }
};

const getBorderColor = (rdv: RdvCaseProps["rdv"]) => {
  const color = getRdvColor(rdv);
  return `color-mix(in srgb, ${color}, black 10%)`;
};

const getFontColor = (rdv: RdvCaseProps["rdv"]) => {
  return ("type" in rdv && fontColor[rdv.type]) || "#333";
};

const getConfirmationStyle = (
  rdv:
    | { tag: "closed" | "empty" | "closedByHoraire" | "fake" }
    | {
        tag: "rdv";
        confirmation_sent: boolean | null;
        confirmation_sent_at: Date | null;
        confirmation_answer_is_positive: boolean | null;
        confirmation_forced: boolean | null;
      },
  now: Date,
) => {
  if (rdv.tag !== "rdv") {
    return "empty";
  }
  if (rdv.confirmation_answer_is_positive || rdv.confirmation_forced) {
    return "confirmed";
  }
  if (rdv.confirmation_sent_at && rdv.confirmation_sent_at > now) {
    return "programmed";
  }
  if (
    rdv.confirmation_sent &&
    rdv.confirmation_sent_at &&
    getDiffInHours(now, rdv.confirmation_sent_at) < 12
  ) {
    return "asked";
  }
  if (
    rdv.confirmation_sent &&
    rdv.confirmation_sent_at &&
    getDiffInHours(now, rdv.confirmation_sent_at) > 12
  ) {
    return "notConfirmed";
  }
  return "empty";
};

export interface RdvCaseProps {
  rdv: (
    | { tag: "closed" | "closedByHoraire" | "fake" }
    | {
        id: string;
        familyColor?: string;
        tag: "rdv";
        confirmation_sent: boolean | null;
        confirmation_sent_at: Date | null;
        confirmation_answer_is_positive: boolean | null;
        confirmation_forced: boolean | null;
        duration: number;
        type: number;
        hour: number;
        minute: number;
        patient_id: string | null; //pas de patient_id pour les rdv fermés
        name: string | null;
        forename: string | null;
        object: string | null;
      }
  ) &
    Timed &
    Dated;

  now: Date;
  heightMultiplier: number;
  onMouseEnter: (
    event: React.MouseEvent<HTMLDivElement>,
    rdvId: string,
  ) => void;
  onMouseLeave: (event: React.MouseEvent) => void;
  onCloseCrossClick: (_id: string) => void;
  onConfirmClick: (_id: string) => void;
  onCaseClick: (rdv: { tag: "rdv"; id: string }) => void;
}

const RdvCase = (props: RdvCaseProps) => {
  //trace(true);

  const { rdv } = props;
  const { heightMultiplier } = props;

  const { setNodeRef } = useDroppable({ id: Transforms.rdvs.getUniqueId(rdv) });
  const highlightedRdv = useAtomValue(highlightedRdvAtom);
  const setHighlightedRdv = useSetAtom(highlightedRdvAtom);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const highlightRequestId =
    rdv.tag === "rdv" && highlightedRdv?.id === rdv.id
      ? highlightedRdv.requestId
      : null;
  const setRdvCaseRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      if (!node || highlightRequestId === null) {
        return;
      }

      const reducesMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      node.scrollIntoView({
        behavior: reducesMotion ? "auto" : "smooth",
        block: "center",
      });
      setIsHighlighted(false);
      window.requestAnimationFrame(() => setIsHighlighted(true));
      setHighlightedRdv((currentHighlight) =>
        currentHighlight?.requestId === highlightRequestId
          ? null
          : currentHighlight,
      );
    },
    [highlightRequestId, setHighlightedRdv, setNodeRef],
  );

  const name = "name" in rdv && rdv.name;
  const forename = "forename" in rdv && rdv.forename;
  const object = "object" in rdv && rdv.object;
  const { onCaseClick, onCloseCrossClick, onConfirmClick } = props;
  const { onMouseEnter, onMouseLeave } = props;
  const { now } = props;
  const hmString = formaters.formatHMObjectForDisplay({
    heure: rdv.hour,
    minute: rdv.minute,
  });
  const isZeroMinute = (hmString: string) => hmString.split(":")[1] === "00";

  const [_, { selectPatientId }] = useSelectedPatientIds();

  //utile pour l'effet de hover sur les cases vides
  const [isHovered, setHover] = useState(false);

  const showCross =
    rdv &&
    "type" in rdv &&
    (rdv.type === 21 || rdv.type === 12 || rdv.type === 28 || rdv.type === 15);
  const handleCrossClick = () => {
    "id" in rdv && onCloseCrossClick(rdv.id);
  };
  const confirmStyle = getConfirmationStyle(rdv, now);

  const handleConfirmClick = () => {
    "id" in rdv && onConfirmClick(rdv.id);
  };
  const background =
    rdv.tag === "rdv" && rdv.familyColor
      ? "linear-gradient(90deg, " +
        rdv.familyColor +
        " 2%, " +
        getRdvColor(rdv) +
        " 2%)"
      : getRdvColor(rdv);

  // const backgroundImage =
  //   rdv.tag === "closed"
  //     ? "repeating-linear-gradient(45deg,transparent,transparent 10px,white 10px,white 12px, transparent 12px)"
  //     : null;
  const color = getFontColor(rdv);

  const style = {
    height: `calc(${heightMultiplier * rdv.duration}em - 1px)`, //on retire 1px pour prendre en compte les margin-top
    background,
    // ...(backgroundImage ? { backgroundImage } : null),
    color,
    borderColor: getBorderColor(rdv),
    anchorName: `--${Transforms.rdvs.getUniqueId(rdv)}`,
  };

  const uniqueID = Transforms.rdvs.getUniqueId(rdv);
  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    //rdv fermé
    if (rdv.tag === "rdv" && !rdv.patient_id) {
      return;
    }
    if (
      rdv.tag === "fake" ||
      rdv.tag === "closed" ||
      rdv.tag === "closedByHoraire"
    ) {
      return;
    }
    event.persist();
    if (rdv.tag === "rdv" && rdv.patient_id) {
      onMouseEnter(event, rdv.id);
    }
  };
  const handleMouseLeave = (event: React.MouseEvent) => {
    // if (rdv.patient === null && !rdv.fake) {
    //   return;
    // }
    if (
      event.relatedTarget &&
      "id" in event.relatedTarget &&
      (event.relatedTarget as HTMLElement).id === "rdvinfopopup"
    ) {
      return;
    }
    setHover(false);
    onMouseLeave(event);
  };

  const handleClick = () => {
    if (Transforms.rdvs.isFakeCase(rdv)) {
      //est-ce que ça ne devrait pas plutôt être géré au niveau du container ?
      return;
    }
    if (Transforms.rdvs.isClosedRdvCase(rdv)) {
      return;
    }
    if (rdv.tag === "rdv" && rdv.patient_id) {
      selectPatientId(rdv.patient_id);
    }
    if (rdv.tag === "rdv") {
      onCaseClick(rdv);
    }
  };
  const handleHighlightAnimationEnd = () => {
    setIsHighlighted(false);
  };
  return (
    <div
      style={style}
      className={classNames({
        [styles.case_rdv_closed]: rdv.tag === "closed",
        [styles.case_rdv_]: rdv.tag !== "closed",
        [styles.cursor_pointer]: rdv.tag !== "closed",
        [styles.spacer]: true,
        [styles.highlighted]: isHighlighted,
      })}
      id={uniqueID}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={"RdvCase"}
      data-type={"type" in rdv && rdv.type}
      ref={setRdvCaseRef}
      onAnimationEnd={handleHighlightAnimationEnd}
    >
      {name || object ? (
        <div>
          {name ? (
            <div
              className={classNames(
                "case_rdv_nom_patient",
                styles.case_rdv_nom_patient,
              )}
            >
              {name && name.toUpperCase()}{" "}
              {formaters.capitaliseFirstLetter(forename || "")}
            </div>
          ) : null}

          <div className="case_rdv_objet">{object}</div>
        </div>
      ) : null}
      <RdvCaseIcons
        showCross={showCross}
        handleCrossClick={handleCrossClick}
        handleConfirmClick={handleConfirmClick}
        confirmationIconStyle={confirmStyle}
      />
    </div>
  );
};

export default RdvCase;
